import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';

import { useVoiceRecorder } from '@/pages/task-redesign/model/useVoiceRecorder';

import type { useTaskCreateForm } from './useTaskCreateForm';
import { toggleId } from './utils';
import { parseInitialBinding } from './desktop/DSmartProcess';
import { DTaskLinkDropdown, DTemplateDropdown, DSmartProcess } from './desktop';
import {
  ACCEPT_BY_KIND,
  RAttachMenu,
  RBindingField,
  RChecklist,
  RClip,
  RClose,
  RDateTimeField,
  RDocChip,
  RFlagCheckbox,
  RHoursField,
  RMediaTile,
  RPrioritySelect,
  RRepeat,
  RRolePicker,
  RVoicePanel,
  RVoicePill,
  formatVoiceLength,
  hoursToHHMM,
  isMediaName,
  isVideoName,
  isVoiceName,
  totalChecklistHours,
  useRoleDepartments,
  type AttachKind,
} from './desktop/redesign';

type FormApi = ReturnType<typeof useTaskCreateForm>;

interface Props {
  form: FormApi;
  isLoading?: boolean;
  mode?: 'create' | 'edit';
  onRequestClose: () => void;
}

const FIELD_BASE =
  'w-full rounded-[10px] bg-[#F6F7FA] px-[14px] py-[13px] text-[12px] text-[#575757] outline-none placeholder:text-[rgba(20,30,49,0.42)]';

/** Читаемая подпись привязки по рефу UF_CRM_TASK: имя записи знает только
 *  блок выбора, поэтому до его ответа показываем тип и номер. */
function bindingLabelFromRef(ref: string) {
  let m = /^D_(\d+)$/.exec(ref);
  if (m) {
    return `Сделка #${m[1]}`;
  }
  m = /^L_(\d+)$/.exec(ref);
  if (m) {
    return `Лид #${m[1]}`;
  }
  m = /^T[0-9A-Fa-f]+_(\d+)$/.exec(ref);
  if (m) {
    return `Смарт-процесс #${m[1]}`;
  }
  return ref;
}

export function TaskCreateModalDesktop({
  form,
  isLoading = false,
  mode = 'create',
  onRequestClose,
}: Props) {
  const canSubmit =
    Boolean(form.title.trim() && form.description.trim()) && !isLoading;

  const [bindOpen, setBindOpen] = useState(false);
  const [bindLabel, setBindLabel] = useState<string | null>(null);
  const [descMenuOpen, setDescMenuOpen] = useState(false);
  const [descRecOpen, setDescRecOpen] = useState(false);
  const [voiceLengths, setVoiceLengths] = useState<Record<string, number>>({});

  const departments = useRoleDepartments();

  // Предвыбранная CRM-привязка из UF_CRM_TASK (напр. при создании задачи из
  // сообщения) — чтобы блок сам «подтянул» данные: смарт "T{e}_{id}",
  // сделка "D_{id}", лид "L_{id}".
  const ufCrmTask = form.watch('UF_CRM_TASK') as string[] | undefined;
  const initialBinding = useMemo(
    () => parseInitialBinding(ufCrmTask),
    [ufCrmTask]
  );

  const shownBindLabel =
    bindLabel ??
    (Array.isArray(ufCrmTask) && ufCrmTask.length > 0
      ? bindingLabelFromRef(String(ufCrmTask[ufCrmTask.length - 1]))
      : 'Без привязки');

  /* ─── Вложения описания ─── */
  const attachInputRef = form.fileInputRef;
  const attachBtnRef = useRef<HTMLButtonElement>(null);

  const openAttachDialog = (kind: AttachKind) => {
    setDescMenuOpen(false);
    const input = attachInputRef.current;
    if (!input) {
      return;
    }
    input.accept = ACCEPT_BY_KIND[kind];
    input.click();
  };

  const descRecorder = useVoiceRecorder({
    onSend: async (blob, filename, durationMs) => {
      const file = new File([blob], filename, { type: blob.type });
      form.setAttachedFiles((prev) => [...prev, file]);
      setVoiceLengths((prev) => ({ ...prev, [filename]: durationMs }));
      setDescRecOpen(false);
    },
  });

  // Превью картинок: object-URL живёт ровно столько, сколько файл в списке.
  const previews = useMemo(() => {
    const map = new Map<File, string>();
    for (const f of form.attachedFiles) {
      if (isMediaName(f.name) && !isVideoName(f.name)) {
        map.set(f, URL.createObjectURL(f));
      }
      if (isVoiceName(f.name)) {
        map.set(f, URL.createObjectURL(f));
      }
    }
    return map;
  }, [form.attachedFiles]);

  useEffect(() => {
    return () => {
      for (const url of previews.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [previews]);

  const removeFileAt = (idx: number) =>
    form.setAttachedFiles((prev) => prev.filter((_, i) => i !== idx));

  const attached = form.attachedFiles.map((file, idx) => ({ file, idx }));
  const descMedia = attached.filter((a) => isMediaName(a.file.name));
  const descVoices = attached.filter((a) => isVoiceName(a.file.name));
  const descDocs = attached.filter(
    (a) => !isMediaName(a.file.name) && !isVoiceName(a.file.name)
  );

  /* ─── Время: из чек-листов или вручную ─── */
  const checklistHoursTotal = totalChecklistHours(form.formChecklist);
  const hoursFromChecklists = checklistHoursTotal > 0;
  const checklistHHMM = hoursToHHMM(checklistHoursTotal);

  useEffect(() => {
    if (!hoursFromChecklists) {
      return;
    }
    if (checklistHHMM !== form.executionTimeHHMM) {
      form.setExecutionTime(checklistHHMM);
    }
    // Синхронизация одностороняя: сумма чек-листов → поле REAL.
  }, [hoursFromChecklists, checklistHHMM]);

  const flags = [
    {
      label: 'Проконтролировать задачу после завершения',
      checked: Boolean(form.watch('CONTROL_AFTER_FINISH')),
      onChange: (checked: boolean) =>
        form.setValue('CONTROL_AFTER_FINISH', checked, {
          shouldValidate: false,
        }),
    },
    {
      label: 'Сохранить как шаблон',
      checked: Boolean(form.watch('saveAsTemplate')),
      onChange: (checked: boolean) =>
        form.setValue('saveAsTemplate', checked, { shouldValidate: false }),
    },
    {
      label: 'Сделать задачу регулярной',
      checked: form.isRecurring,
      onChange: (checked: boolean) => form.setIsRecurring(checked),
    },
  ];

  const responsibleId = form.responsibleId ? Number(form.responsibleId) : null;
  const creatorId = Number(form.creatorLocalId ?? form.user) || null;

  const uniq = (ids: number[]) => Array.from(new Set(ids));

  return (
    <div
      className={clsx(
        'my-auto w-full max-w-[660px] rounded-[22px] bg-white px-[24px] pb-[26px] pt-[22px]',
        'font-sans shadow-[0_20px_60px_rgba(20,30,49,0.12)]',
        'animate-in fade-in-0 zoom-in-95 duration-300 ease-out'
      )}
      role="dialog"
      aria-modal="true"
      aria-label={
        mode === 'create' ? 'Создание задачи' : 'Редактирование задачи'
      }
      onClick={(e) => e.stopPropagation()}
    >
      {/* Шапка: подпись и выбор шаблона (на мобайле — словом в заголовке) */}
      <div className="flex flex-wrap items-center gap-[12px]">
        <div className="min-w-0 flex-1 text-[12px] font-bold text-[#141E31]">
          {mode === 'create' ? (
            <>
              Создайте задачу или выберите{' '}
              <span className="hidden min-[620px]:inline">шаблон</span>
              <span className="inline-flex min-[620px]:hidden">
                <DTemplateDropdown
                  variant="inline"
                  disabled={isLoading}
                  onApplyTemplate={form.applyTemplate}
                />
              </span>
            </>
          ) : (
            'Редактирование задачи'
          )}
        </div>
        {mode === 'create' ? (
          <span className="hidden min-[620px]:flex">
            <DTemplateDropdown
              variant="proto"
              disabled={isLoading}
              onApplyTemplate={form.applyTemplate}
            />
          </span>
        ) : null}
        <button
          type="button"
          onClick={onRequestClose}
          className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[8px] transition hover:bg-[rgba(59,77,127,0.07)]"
          aria-label="Закрыть"
          title="Закрыть"
        >
          <RClose size={16} />
        </button>
      </div>

      {/* Привязка задачи и приоритет */}
      <div className="mt-[16px] flex flex-wrap items-center gap-x-[18px] gap-y-[14px]">
        <RBindingField
          label={shownBindLabel}
          open={bindOpen}
          onToggle={() => setBindOpen((v) => !v)}
          disabled={isLoading}
        >
          <DSmartProcess
            disabled={isLoading}
            initialBinding={initialBinding}
            onApply={(project) => {
              form.applyCrmBinding(project);
              setBindLabel(
                project.title?.String?.trim() || `Смарт-процесс #${project.id}`
              );
              setBindOpen(false);
            }}
            onApplyRef={(ref) => {
              form.applyCrmRef(ref);
              setBindLabel(bindingLabelFromRef(ref));
              setBindOpen(false);
            }}
          />
        </RBindingField>

        <RPrioritySelect
          value={form.watch('PRIORITY') ?? null}
          onChange={(v) =>
            form.setValue('PRIORITY', form.clampPriority(v), {
              shouldValidate: false,
            })
          }
          disabled={isLoading}
        />
      </div>

      {/* Название */}
      <input
        value={form.title}
        disabled={isLoading}
        onChange={(e) =>
          form.setValue('TITLE', e.target.value, { shouldValidate: true })
        }
        placeholder="Название задачи (Обязательное поле)"
        className={clsx(
          FIELD_BASE,
          'mt-[16px] border',
          form.title.trim()
            ? 'border-[rgba(59,77,127,0.14)]'
            : 'border-[rgba(59,77,127,0.10)]',
          isLoading && 'cursor-not-allowed opacity-50'
        )}
      />
      {form.errors?.TITLE ? (
        <div className="mt-[6px] text-[11px] text-[#EF4642]">
          {String(form.errors.TITLE.message ?? '')}
        </div>
      ) : null}

      {/* Описание с вложениями */}
      <div className="relative mt-[10px]">
        <textarea
          value={form.description}
          disabled={isLoading}
          rows={3}
          onChange={(e) =>
            form.setValue('DESCRIPTION', e.target.value, {
              shouldValidate: true,
            })
          }
          placeholder="Описание задачи. Можете подкрепить файлы или скриншоты (Обязательное поле)"
          className={clsx(
            FIELD_BASE,
            'resize-y border pr-[40px] leading-[1.5]',
            form.description.trim()
              ? 'border-[rgba(59,77,127,0.14)]'
              : 'border-[rgba(59,77,127,0.10)]',
            isLoading && 'cursor-not-allowed opacity-50'
          )}
        />

        <input
          ref={attachInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) {
              form.setAttachedFiles((prev) => [...prev, ...files]);
            }
            e.currentTarget.value = '';
          }}
        />

        <button
          ref={attachBtnRef}
          type="button"
          disabled={isLoading}
          onClick={() => setDescMenuOpen((v) => !v)}
          className={clsx(
            'absolute right-[10px] top-[10px] flex h-[26px] w-[26px] items-center justify-center rounded-[8px]',
            'transition hover:bg-[rgba(59,77,127,0.07)]',
            isLoading && 'cursor-not-allowed opacity-50'
          )}
          aria-label="Прикрепить"
          title="Прикрепить"
        >
          <RClip />
        </button>

        {descMenuOpen ? (
          <RAttachMenu
            anchorRef={attachBtnRef}
            align="end"
            sideOffset={6}
            alignOffset={-2}
            onPick={openAttachDialog}
            onVoice={() => {
              setDescMenuOpen(false);
              setDescRecOpen(true);
              void descRecorder.start();
            }}
          />
        ) : null}

        {descRecOpen ? (
          <RVoicePanel
            anchorRef={attachBtnRef}
            align="end"
            sideOffset={6}
            alignOffset={-2}
            clipId="recclip-desc"
            elapsedMs={descRecorder.elapsedMs}
            busy={descRecorder.state === 'sending'}
            onCancel={() => {
              descRecorder.cancel();
              setDescRecOpen(false);
            }}
            onSave={descRecorder.stopAndSend}
          />
        ) : null}
      </div>

      {descMedia.length > 0 ? (
        <div className="mt-[10px] flex flex-wrap items-start gap-[8px]">
          {descMedia.map(({ file, idx }) => (
            <RMediaTile
              key={`${file.name}_${idx}`}
              name={file.name}
              video={isVideoName(file.name)}
              previewUrl={previews.get(file) ?? null}
              onRemove={() => removeFileAt(idx)}
            />
          ))}
        </div>
      ) : null}

      {descVoices.length > 0 ? (
        <div className="mt-[10px] flex flex-col gap-[6px]">
          {descVoices.map(({ file, idx }) => (
            <RVoicePill
              key={`${file.name}_${idx}`}
              length={formatVoiceLength(voiceLengths[file.name] ?? 0)}
              src={previews.get(file) ?? null}
              onRemove={() => removeFileAt(idx)}
            />
          ))}
        </div>
      ) : null}

      {descDocs.length > 0 ? (
        <div className="mt-[10px] flex flex-wrap items-center gap-[6px]">
          {descDocs.map(({ file, idx }) => (
            <RDocChip
              key={`${file.name}_${idx}`}
              name={file.name}
              onRemove={() => removeFileAt(idx)}
            />
          ))}
        </div>
      ) : null}

      {/* Роли участников */}
      <div className="mt-[26px] grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-x-[28px] gap-y-[24px]">
        <RRolePicker
          label="Исполнитель"
          items={form.people}
          selectedIds={responsibleId ? [responsibleId] : []}
          onPick={(id) =>
            form.setValue('RESPONSIBLE_ID', id, { shouldValidate: true })
          }
          disabled={isLoading}
        />
        <RRolePicker
          label="Постановщик"
          items={form.people}
          selectedIds={creatorId ? [creatorId] : []}
          onPick={(id) => form.setCreatorLocalId(id)}
          disabled={isLoading}
        />
        <RRolePicker
          label="Помощники"
          multiple
          items={form.people}
          selectedIds={form.accompliceIds}
          onPick={(id) =>
            form.setAccomplicesIds(toggleId(form.accompliceIds, id))
          }
          onRemove={(id) =>
            form.setAccomplicesIds(form.accompliceIds.filter((x) => x !== id))
          }
          departments={departments}
          onAddMany={(ids) =>
            form.setAccomplicesIds(uniq([...form.accompliceIds, ...ids]))
          }
          disabled={isLoading}
        />
        <RRolePicker
          label="Наблюдатели"
          multiple
          items={form.people}
          selectedIds={form.auditorIds}
          onPick={(id) => form.setAuditorsIds(toggleId(form.auditorIds, id))}
          onRemove={(id) =>
            form.setAuditorsIds(form.auditorIds.filter((x) => x !== id))
          }
          departments={departments}
          onAddMany={(ids) =>
            form.setAuditorsIds(uniq([...form.auditorIds, ...ids]))
          }
          disabled={isLoading}
        />
      </div>

      {/* Сроки и оценка */}
      <div className="mt-[28px] grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-x-[28px] gap-y-[20px]">
        <span className="flex min-w-0 items-center gap-[10px]">
          <span className="flex-1 whitespace-nowrap text-[11px] text-[rgba(20,30,49,0.7)] min-[620px]:text-[12px]">
            Крайний срок
          </span>
          <RDateTimeField
            title="Крайний срок"
            value={form.deadline}
            initToNow={!form.hasInitialDeadline}
            onChange={(v) =>
              form.setValue('DEADLINE', v, { shouldValidate: true })
            }
            disabled={isLoading}
          />
        </span>

        <span className="flex min-w-0 items-center gap-[10px]">
          <span className="flex-1 whitespace-nowrap text-[11px] text-[rgba(20,30,49,0.7)] min-[620px]:text-[12px]">
            Напомнить заранее?
          </span>
          <RDateTimeField
            title="Напомнить заранее"
            value={(form.watch('REMIND_AT') ?? '') as string}
            onChange={(v) =>
              form.setValue('REMIND_AT', v, { shouldValidate: false })
            }
            disabled={isLoading}
          />
        </span>

        <RHoursField
          value={form.executionTimeHHMM}
          onChange={(v) => form.setExecutionTime(v)}
          readOnly={hoursFromChecklists}
          disabled={isLoading}
        />
      </div>

      {/* Чек-листы */}
      <div className="mt-[26px]">
        <RChecklist
          value={form.formChecklist}
          onChange={form.setFormChecklist}
          people={form.people}
          disabled={isLoading}
        />
      </div>

      {/* Связь с другой задачей */}
      <div className="mt-[26px] flex items-center gap-[10px]">
        <span className="text-[12px] text-[rgba(20,30,49,0.7)]">
          Связать с другой задачей?
        </span>
        <DTaskLinkDropdown
          variant="proto"
          disabled={isLoading}
          tasks={form.taskOptions}
          selectedId={form.parentSelectedId}
          excludeId={form.currentTaskId || null}
          onSelect={(id) =>
            form.setValue('PARENT_ID', id ?? null, {
              shouldValidate: false,
              shouldDirty: true,
            })
          }
          placeholder="Выбор задачи"
        />
      </div>

      {/* Флаги */}
      <div
        className={clsx(
          'mt-[26px] grid gap-x-[28px] gap-y-[16px] border-t border-[rgba(59,77,127,0.10)] pt-[22px]',
          'grid-cols-2 min-[620px]:grid-cols-[repeat(auto-fit,minmax(200px,auto))]'
        )}
      >
        {flags.map((f) => (
          <RFlagCheckbox
            key={f.label}
            checked={f.checked}
            onChange={f.onChange}
            label={f.label}
            disabled={isLoading}
          />
        ))}
      </div>

      <RRepeat
        isOpen={form.isRecurring}
        disabled={isLoading}
        period={form.recurrencePeriod}
        setPeriod={form.setRecurrencePeriod}
        firstAt={form.recurrenceFirstAt}
        setFirstAt={form.setRecurrenceFirstAt}
        everyCount={form.recurrenceEvery}
        setEveryCount={form.setRecurrenceEvery}
        untilAt={form.recurrenceUntilAt}
        setUntilAt={form.setRecurrenceUntilAt}
      />

      <div className="mt-[30px] flex justify-center">
        <button
          type="submit"
          disabled={!canSubmit}
          className={clsx(
            'min-w-[168px] rounded-full bg-[#57B792] px-[20px] py-[10px]',
            'text-[12px] font-bold text-white transition',
            canSubmit
              ? 'shadow-[0_6px_16px_rgba(87,183,146,0.32)] hover:bg-[#4aa985]'
              : 'cursor-not-allowed opacity-60'
          )}
        >
          {isLoading
            ? 'Отправка...'
            : mode === 'create'
              ? 'Создать задачу'
              : 'Сохранить'}
        </button>
      </div>
    </div>
  );
}
